import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ClerkProvider, SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react';

import ScrollToTop from './components/ScrollToTop';
import SignInPage, { localization } from './components/SignIn';

import './App.css';
import './homev2/homev2.css';
import { articles } from './data/blogArticles';

import HomePage from './homev2/HomePage';
import ErstanalysePage from './homev2/ErstanalysePage';
import MieterselbstauskunftPage from './homev2/MieterselbstauskunftPage';
import TerminBuchenPage from './homev2/TerminBuchenPage';
import MietpreisAnalysePage from './homev2/tools/MietpreisAnalysePage';
import AnfrageForm from './homev2/AnfrageForm';
// DEACTIVATED (code retained) — old Erstanalyse funnel. Uncomment these imports
// and the routes below to re-enable:
// import FunnelPage from './homev2/FunnelPage';
// import FunnelDanke from './homev2/FunnelDanke';
import NotFound from './homev2/NotFound';
import BlogOverview from './homev2/BlogOverview';
import Blog from './homev2/Blog';
import { ImpressumPage, DatenschutzPage, AGBPage } from './homev2/Legal';
import { ConsentBanner } from './homev2/Consent';

const PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

const Dashboard = lazy(() => import('./dashboard/dashboard/Dashboard'));

function App() {
  return (
    <HelmetProvider>
      <ClerkProvider publishableKey={PUBLISHABLE_KEY} signInUrl="/login" localization={localization}>
        <Router>
          <ScrollToTop />
          <AppContent />
        </Router>
      </ClerkProvider>
    </HelmetProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const isDashboardRoute = location.pathname.startsWith('/dashboard');
  const homev2Active = !isDashboardRoute;

  // Per-route titles are owned by each page via useDocumentTitle (see shared.js).
  // Dashboard is the one non-homev2 route, so we set its title here.
  useEffect(() => {
    if (isDashboardRoute) {
      document.title = 'Dashboard — hamba';
    }
  }, [isDashboardRoute]);

  const appClass = [
    isDashboardRoute ? 'App App--dashboard' : 'App',
    homev2Active ? 'homev2' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={appClass}>
      {/* Die Hinweisleiste ueber der Navigation ist abgeschaltet. Komponente und
          Stile bleiben erhalten (AnalyseLeiste in homev2/shared.js, .topbar in
          homev2.css); zum Wiedereinschalten hier einsetzen und --bar-h in
          homev2.css auf die Leistenhoehe zuruecksetzen. */}
      <main className="App-content">
        <Suspense fallback={<div className="route-loading" aria-busy="true" />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/erstanalyse" element={<ErstanalysePage />} />
            {/* Old Erstanalyse funnel — DEACTIVATED (code retained in homev2/FunnelPage.js
                + FunnelDanke.js). Uncomment the imports above and these routes to re-enable.
            <Route path="/funnel" element={<FunnelPage />} />
            <Route path="/funnel/danke" element={<FunnelDanke />} /> */}
            <Route path="/mieterselbstauskunft" element={<MieterselbstauskunftPage />} />
            <Route path="/termin-buchen" element={<TerminBuchenPage />} />
            <Route path="/kostenlose-mietpreis-analyse" element={<MietpreisAnalysePage />} />
            {/* Preview only — two-step Erstanalyse form (not yet linked from any CTA). */}
            <Route path="/anfrage" element={<AnfrageForm />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/datenschutz" element={<DatenschutzPage />} />
            <Route path="/agb" element={<AGBPage />} />
            <Route path="/kontakt" element={<Navigate to="/" replace />} />

            <Route path="/blog" element={<BlogOverview articles={articles} />} />
            <Route path="/blog/:slug" element={<Blog articles={articles} />} />

            <Route path="/login" element={<SignInPage />} />
            <Route
              path="/dashboard"
              element={
                <>
                  <SignedIn><Dashboard /></SignedIn>
                  <SignedOut><RedirectToSignIn /></SignedOut>
                </>
              }
            />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <ConsentBanner />
    </div>
  );
}

export default App;
